import type { VisitOptions } from "@inertiajs/core";

const SKIPPED_TRANSITION_NAMES = new Set(["AbortError", "InvalidStateError"]);

function isSkippedTransition(error: unknown): boolean {
  return error instanceof DOMException && SKIPPED_TRANSITION_NAMES.has(error.name);
}

function isBackgroundRefresh(options: VisitOptions): boolean {
  return Boolean(options.async || options.prefetch || options.only?.length);
}

/**
 * The browser skips a transition whenever a second visit starts mid-animation or the
 * document stops being renderable (tab hidden mid-capture). CSS View Transitions L1 rejects
 * `ready` on skip WITHOUT marking it handled, and Inertia drops the ViewTransition object
 * entirely when `viewTransition` is a boolean — so the only way to attach a handler is to
 * pass this callback. The DOM swap still runs on a skip; there is nothing to recover.
 *
 * Rethrowing anything else is deliberate: a swap that genuinely fails must still reach
 * Sentry, so do not collapse these into bare `catch(() => {})`.
 */
export function quietViewTransition(transition: ViewTransition): void {
  const rethrowRealFailure = (error: unknown) => {
    if (!isSkippedTransition(error)) throw error;
  };

  transition.ready.catch(rethrowRealFailure);
  transition.updateCallbackDone.catch(rethrowRealFailure);
  transition.finished.catch(rethrowRealFailure);
}

/**
 * Decides which visits morph the document. Only user-initiated full-page navigations do:
 * background prop refreshes (notification counts, favourite resyncs, InfiniteScroll appends)
 * would suppress rendering document-wide for no visual gain, and are what collide with an
 * in-flight navigation transition in the first place.
 */
export function resolveViewTransition(options: VisitOptions): VisitOptions["viewTransition"] {
  // Inertia spreads the `visitOptions` result AFTER the per-call options (getPendingVisit in
  // @inertiajs/core), so honouring an explicit `false` here is the only thing keeping the
  // per-call opt-out — and Inertia's own prefetch opt-out — working at all.
  if (options.viewTransition === false) return false;
  if (isBackgroundRefresh(options)) return false;

  return quietViewTransition;
}
